import React from 'react';
import { Box, CircularProgress, LinearProgress, Typography, Alert } from '@mui/material';

/**
 * GridLoadingOverlay
 * 
 * Displays a loading overlay with progress indication during grid data loading.
 * Shows current operation, progress bar, and any errors.
 * Stays visible until all data is fully loaded to prevent premature user interaction.
 * 
 * @param {Object} loadingState - State from GridLoadingOrchestrator
 * @param {boolean} loadingState.isLoading - Whether any operations are loading
 * @param {number} loadingState.progress - Progress percentage (0-100)
 * @param {string} loadingState.currentOperation - Currently executing operation
 * @param {Array} loadingState.failedOperations - List of failed operations
 */
export default function GridLoadingOverlay({ loadingState }) {
  // Show overlay if loading OR if progress is not yet 100%
  // This prevents the overlay from disappearing before all operations complete
  if (!loadingState || (!loadingState.isLoading && loadingState.progress === 100)) {
    return null;
  }

  const operationLabels = {
    'project-info': 'Loading project information...',
    'entities': 'Loading entities...',
    'master-stats': 'Loading Master Stats...',
    'departments': 'Loading departments...',
    'accounts': 'Loading accounts...',
    'changes': 'Loading changes...',
    'grid-setup': 'Setting up grid...',
    'master-stats-refresh': 'Refreshing Master Stats...',
    'processing-change': 'Processing change...',
  };

  const currentOperationLabel = loadingState.currentOperation
    ? operationLabels[loadingState.currentOperation] || 'Loading...'
    : loadingState.progress < 100 ? 'Finalizing...' : 'Initializing...';

  const hasErrors = loadingState.failedOperations && loadingState.failedOperations.length > 0;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.97)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(3px)',
      }}
    >
      <CircularProgress size={60} thickness={4} />
      
      <Typography 
        variant="h6" 
        sx={{ 
          mt: 3,
          fontWeight: 500,
          color: 'text.primary'
        }}
      >
        {currentOperationLabel}
      </Typography>
      
      <Box sx={{ width: '400px', mt: 3 }}>
        <LinearProgress 
          variant="determinate" 
          value={loadingState.progress}
          sx={{
            height: 8,
            borderRadius: 4,
          }}
        />
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ mt: 1, textAlign: 'center' }}
        >
          {loadingState.progress}% complete
        </Typography>
      </Box>

      {loadingState.progress < 100 && (
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ mt: 2, fontStyle: 'italic' }}
        >
          Please wait - buttons will be enabled when loading completes
        </Typography>
      )}

      {hasErrors && (
        <Alert 
          severity="warning" 
          sx={{ mt: 3, maxWidth: '400px' }}
        >
          Some operations failed. The grid may not display all data.
        </Alert>
      )}
    </Box>
  );
}
